import { useState } from "react"
import { Activity } from "../types/ActivityTypes";
import { Box, Button, Checkbox, FormControl, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { typologies } from "../contexts/constants/typologies";
import { unitsMeasure, unitsProduction } from "../contexts/constants/unityOfMeasurement";


interface ActivityFormProps {
    onAddActivity: (activity: Activity) => void;
}

const ActivityForm: React.FC<ActivityFormProps> = ({ onAddActivity }) => {
    const [formData, setFormData] = useState<Activity>({
        id: 0,
        name: '',
        typology: '',
        section: '',
        cip: '',
        addProduction: false,
        addMh: false,
        subActivity: '',
        unitOfMeasurement: '',
        unitOfMeasurementProduction: '',
    });

    const typologiesArray = Object.keys(typologies) as Array<keyof typeof typologies>;
    const unitMeasureArray = Object.keys(unitsMeasure) as Array<keyof typeof unitsMeasure>
    const unitProductionArray = Object.keys(unitsProduction) as Array<keyof typeof unitsProduction>
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [formErrors, setFormErrors] = useState({
        typology: false,
        section: false,
        name: false,
        cip: false,
        unitsMeasure: false,
        unitsProduction: false
      });

    const options = ['Horas Hombre', 'Produccion'];

    const handleTypologyChange = (e: SelectChangeEvent<string>) => {
        setFormData({
            ...formData,
            typology: e.target.value
        });

        setFormErrors((prevErrors) => ({
            ...prevErrors,
            typology: !e,
        }));
    }

    const handleUnitMeasureChange = (e: SelectChangeEvent<string>) => {
        setFormData({
            ...formData,
            unitOfMeasurement: e.target.value
        });

        setFormErrors((prevErrors) => ({
            ...prevErrors,
            unitsMeasure: !e,
        }));
    }

    const handleUnitProductionChange = (e: SelectChangeEvent<string>) => {
        setFormData({
            ...formData,
            unitOfMeasurementProduction: e.target.value
        });

        setFormErrors((prevErrors) => ({
            ...prevErrors,
            unitsProduction: !e,
        }));
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        /*setFormData({ ...formData, [name]: value });

        setFormErrors((prevErrors) => ({
            ...prevErrors,
            [name]: !value,
        }));*/
        if (name === 'cip') {
            setFormData({ ...formData, [name]: value });
        
            if (!validateCIP(value)) {
              setFormErrors((prevErrors) => ({
                ...prevErrors,
                cip: true // Marcar error si el formato no coincide
              }));
              setErrorMessage('CIP no válido. Debe seguir el formato LTX.YY.ZT1.');
            } else {
              setFormErrors((prevErrors) => ({
                ...prevErrors,
                cip: false // Limpiar error si el formato es correcto
              }));
              setErrorMessage('');
            }
          } else {
            setFormData({ ...formData, [name]: value });
            // Limpiar errores en otros campos al ingresar datos
            setFormErrors((prevErrors) => ({
              ...prevErrors,
              [name]: !value
            }));
          }
    };

    const handleSumChange = (opt: SelectChangeEvent<string[]>) => {
        const value = opt.target.value as string[];
        setSelectedOptions(value);
    };

    const validateForm = () => {
        const errors = {
          typology: !formData.typology,
          section: !formData.section,
          name: !formData.name,
          cip: !formData.cip,
          unitsMeasure: !formData.unitOfMeasurement,
          unitsProduction: !formData.unitOfMeasurementProduction

        };
        setFormErrors(errors);
        
        // Si no hay errores, retorna true
        return !Object.values(errors).includes(true);
    };

    const validateCIP = (cip: string): boolean => {
        // Expresión regular para validar el formato CIP
        const cipRegex = /^[A-Za-z]{3}\.[0-9]{2}\.[A-Za-z]{1}1$/;
        return cipRegex.test(cip);
    };

    const handleAddClick = () => {
       
        if (validateForm()) {
            try {
                const addHh = selectedOptions.includes('Horas Hombre');
                const addProd = selectedOptions.includes('Produccion');

                const dataWithSum = {
                    ...formData,
                    addMh : addHh,
                    addProduction : addProd
                }
                onAddActivity(dataWithSum);

                setFormData({
                    id: 0,
                    name: '',
                    typology: '',
                    section: '',
                    cip: '',
                    addProduction: false,
                    addMh: false,
                    subActivity: '',
                    unitOfMeasurement: '',
                    unitOfMeasurementProduction: '',
                });
                setErrorMessage('');
            } catch {
                setErrorMessage('Error al agregar el usuario. Inténtalo de nuevo.');
            }
        } else {
            setErrorMessage('Por favor, llena todos los campos obligatorios.');
        }
    };

    return (
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 2, border: '1px solid #ddd' }}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Tipología</InputLabel>
                        <Select
                            value={formData.typology || ''}
                            onChange={handleTypologyChange}
                            label="Tipología"
                            required
                        >
                            {typologiesArray.map((typology) => (
                                <MenuItem key={typology} value={typology}>
                                    {typologies[typology]}
                                </MenuItem>
                            ))}
                        </Select>
                        {formErrors.typology && <Typography color="error">Este campo es obligatorio</Typography>}
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField label="Sección" name="section" value={formData.section} onChange={handleInputChange} variant="outlined" fullWidth required error={formErrors.section} helperText={formErrors.section? 'Este campo es obligatorio' : ''}/>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField label="Actividad" name="name" value={formData.name} onChange={handleInputChange} variant="outlined" fullWidth required error={formErrors.name} helperText={formErrors.name ? 'Este campo es obligatorio' : ''}/>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField label="Subactividad" name="subActivity" value={formData.subActivity} onChange={handleInputChange} variant="outlined" fullWidth />
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Unidad de producción</InputLabel>
                        <Select
                            value={formData.unitOfMeasurementProduction || ''}
                            onChange={handleUnitProductionChange}
                            label="Unidad de Producción"
                            
                        >
                            {unitProductionArray.map((unit) => (
                                <MenuItem key={unit} value={unit}>
                                    {unitsProduction[unit].name} 
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Unidad de avance</InputLabel>
                        <Select
                            value={formData.unitOfMeasurement || ''}
                            onChange={handleUnitMeasureChange}
                            label="Unidad de avance"
                            
                        >
                            {unitMeasureArray.map((unit) => (
                                <MenuItem key={unit} value={unit}>
                                    {unitsMeasure[unit].name} 
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField label="CIP" name="cip" value={formData.cip} onChange={handleInputChange} variant="outlined" fullWidth required error={formErrors.cip} helperText={formErrors.cip ? 'Este campo es obligatorio' : ''}/>
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Suma HH/Producción</InputLabel>
                        <Select
                          multiple
                          value={selectedOptions}
                          onChange={handleSumChange}
                          input={<OutlinedInput label="Selecciona Opciones" />}
                          renderValue={(selected) => (selected as string[]).join(', ')}
                        >
                          {options.map((option) => (
                            <MenuItem key={option} value={option}>
                              <Checkbox checked={selectedOptions.indexOf(option) > -1} />
                              <ListItemText primary={option === 'Horas Hombre' ? 'Horas Hombre' : 'Producción'} />
                            </MenuItem>
                          ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <Button variant="contained" color="primary" onClick={handleAddClick} fullWidth>
                        Agregar
                    </Button>
                </Grid>
                {errorMessage && (
                    <Grid  item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography color="error">{errorMessage}</Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );


};

export default ActivityForm;